import {
    asyncListWithSkipTakeCreate,
    cachedValueCreateWithParamsV2,
    Evaluable,
    evaluateWhenFunction,
    hashFunctionCreate,
    stringReplaceEmptyOrWhiteSpaceValue,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import type {DoppeDtoStoreProduct, DoppeDtoStoreProductType} from '../../../client-server-common/types/doppe-dto-store-product';
import type {IDoppeViewerApi} from '../../../client-server-common/api/doppe-viewer-api';

/**
 * Creates a cached factory for fetching store products async lists.
 * The factory caches async lists by search and productType parameters,
 * so multiple calls with the same params return the same cached list.
 * 
 * @param viewerApiClientGetter - A getter function for the viewer API client
 * @returns A cached value factory for products async lists
 * 
 * @example
 * ```ts
 * const productsAsyncListFactory = useMemo(() => {
 *     return doppeViewerAsyncListFactoryProducts(() => viewerApiClient);
 * }, [viewerApiClient]);
 * 
 * const productsAsyncList = productsAsyncListFactory.getValue({
 *     search: searchText,
 *     productType: DoppeDtoStoreProductType.Digital
 * });
 * ```
 */
export function doppeViewerAsyncListFactoryProducts(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryProducts.Params>({
            search: hashFunctionCreate(),
            productType: hashFunctionCreate(),
        }),
        paramsNormalizationFunction: params => ({
            ...params,
            search: valueConvertWhenNotNullable(params.search, s =>
                stringReplaceEmptyOrWhiteSpaceValue(s, undefined)
            )
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            return asyncListWithSkipTakeCreate<DoppeDtoStoreProduct>({
                fetchFunction: async (skip, take) => {
                    return viewerApiClient.getStoreProducts({
                        skip,
                        take,
                        search: params.search,
                        productType: params.productType,
                    });
                },
                defaultTake: 30,
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryProducts {
    export interface Params {
        /**
         * Search query to filter products by name.
         */
        search?: string;
        
        /**
         * Filter products by type (Physical, Digital).
         */
        productType?: DoppeDtoStoreProductType;
    }
}
